import type { RoleResponse } from '@/features/auth/domain/auth.types'
import { SYSTEM_ROLE_CODE } from '@/shared/domain/system-roles'

export const USER_ROLES = ['inspector', 'admin', 'super_admin', 'sales_manager'] as const

export type UserRole = (typeof USER_ROLES)[number]

export const ROLE_LABEL_KEYS: Record<UserRole, string> = {
  inspector: 'app.roleInspector',
  admin: 'app.roleAdmin',
  super_admin: 'app.roleSuperAdmin',
  sales_manager: 'app.roleSalesManager',
}

const ADMIN_ROLES = ['admin', 'super_admin'] as const
const DASHBOARD_ROLES = ['super_admin'] as const
const OPERATIONS_ROLES = ['inspector', 'admin', 'super_admin'] as const
const SALES_ROLES = ['super_admin', 'sales_manager'] as const
const SUPER_ADMIN_ROLES = ['super_admin'] as const

const ROUTE_ALLOWED_ROLES: Record<string, readonly UserRole[]> = {
  dashboard: DASHBOARD_ROLES,
  'sales-dashboard': SALES_ROLES,
  'sales-egg-batches': SALES_ROLES,
  'sales-egg-batch-detail': SALES_ROLES,
  'planned-batches': SALES_ROLES,
  'planned-batch-new': SALES_ROLES,
  'planned-batch-edit': SALES_ROLES,
  orders: SALES_ROLES,
  'orders-hatch-calendar': SALES_ROLES,
  'order-detail': SALES_ROLES,
  'order-new': SALES_ROLES,
  'order-edit': SALES_ROLES,
  customers: SALES_ROLES,
  'customer-detail': SALES_ROLES,
  payments: SALES_ROLES,
  'payment-detail': SALES_ROLES,
  debtors: SALES_ROLES,
  creditors: SALES_ROLES,
  'wallet-transaction-detail': SALES_ROLES,
  reminders: SALES_ROLES,
  'price-surveys': SALES_ROLES,
  'price-survey-history': SALES_ROLES,
  'price-survey-new': SALES_ROLES,
  'price-survey-detail': SALES_ROLES,
  'price-survey-edit': SALES_ROLES,
  competitors: SALES_ROLES,
  'competitor-history': SALES_ROLES,

  'admin-users': ADMIN_ROLES,
  'admin-users-edit': ADMIN_ROLES,
  'admin-roles': ADMIN_ROLES,
  'admin-role-permissions': ADMIN_ROLES,

  settings: ADMIN_ROLES,
  'settings-branches': ADMIN_ROLES,
  'settings-suppliers': ADMIN_ROLES,
  'settings-incubation-machines': ADMIN_ROLES,
  'settings-egg-standards': ADMIN_ROLES,
  'expense-categories': SUPER_ADMIN_ROLES,
  'settings-vaccines': ADMIN_ROLES,

  expenses: SUPER_ADMIN_ROLES,
  'expense-detail': SUPER_ADMIN_ROLES,
  'egg-batches': OPERATIONS_ROLES,
  'egg-batch-create': OPERATIONS_ROLES,
  'egg-batch-detail': OPERATIONS_ROLES,
  'egg-batch-edit': OPERATIONS_ROLES,
  sorting: OPERATIONS_ROLES,
  'setting-flow': OPERATIONS_ROLES,
  'setting-machine-detail': OPERATIONS_ROLES,
  'setting-machine-load': OPERATIONS_ROLES,
  'hatching-flow': OPERATIONS_ROLES,
  'branch-orders': OPERATIONS_ROLES,
  'hatching-machine-detail': OPERATIONS_ROLES,
  'hatching-machine-load': OPERATIONS_ROLES,
}

// Migration to operation-code gating (auth.md §6): routes listed here are gated on the backend
// operation code from `/auth/me` `permissions`. Routes NOT listed fall back to the legacy
// role-based gating in ROUTE_ALLOWED_ROLES — this hybrid is the incremental migration state.
const ROUTE_REQUIRED_PERMISSION: Record<string, string> = {
  expenses: 'expenses:read',
  'expense-detail': 'expenses:read',
  'expense-categories': 'expense-categories:read',
  // Renamed by the 2026-07-19 backend reorg (hatchery-changelog.md): section `dashboard` became
  // `hatchery-dashboard`, and `dashboard:read` no longer exists at all. Because a mapped permission
  // is checked with a bare `permissions.includes()` — no super-admin escape — the stale code locked
  // every user out of the dashboard, which is also LANDING_PRIORITY[0] and the "/" redirect target.
  dashboard: 'hatchery-dashboard:read',

  // The sales batch screens moved off `/orders/*` onto the new `sales-dashboard` section, which
  // carries its own permission (dashboard.md §1). The commercial panel added 2026-07-19 reuses it
  // unchanged (dashboard.md §8) — nothing to re-grant.
  'sales-dashboard': 'sales-dashboard:read',
  'sales-egg-batches': 'sales-dashboard:read',
  'sales-egg-batch-detail': 'sales-dashboard:read',
  // Section `planned-batches`, SALES group (planned-batches.md §5) — the four codes are live on the
  // backend, verified against /auth/me.
  'planned-batches': 'planned-batches:read',
  'planned-batch-new': 'planned-batches:create',
  'planned-batch-edit': 'planned-batches:update',
  orders: 'orders:read',
  'orders-hatch-calendar': 'sales-dashboard:read',
  'order-detail': 'orders:read',
  'order-new': 'orders:create',
  'order-edit': 'orders:update',
  customers: 'customers:read',
  'customer-detail': 'customers:read',
  payments: 'payments:read',
  'payment-detail': 'payments:read',
  // Section `debtors`, SALES group (ERP-Backend docs/changes/debtors.md) — a permission of its own,
  // so who may see what customers owe is granted apart from who may see the customers.
  debtors: 'debtors:read',
  // Section `creditors`, SALES group (ERP-Backend docs/changes/creditors.md) — granted on its own too.
  creditors: 'creditors:read',
  'wallet-transaction-detail': 'wallets:read',
  reminders: 'reminders:read',
  // Sections `price-surveys` / `competitors`, SALES group (competitor-price-surveys.md §4).
  'price-surveys': 'price-surveys:read',
  'price-survey-history': 'price-surveys:read',
  'price-survey-new': 'price-surveys:create',
  'price-survey-detail': 'price-surveys:read',
  'price-survey-edit': 'price-surveys:update',
  competitors: 'competitors:read',
  // Price movement is market data: `competitors:read` alone does not open it
  // (competitor-price-movement.md §4).
  'competitor-history': 'price-surveys:read',

  'settings-branches': 'branches:read',
  'settings-suppliers': 'suppliers:read',
  'settings-incubation-machines': 'incubation-machines:read',
  'settings-egg-standards': 'egg-standards:read',
  'settings-vaccines': 'vaccines:read',

  'egg-batches': 'batches:read',
  'egg-batch-create': 'batches:create',
  'egg-batch-detail': 'batches:read',
  'egg-batch-edit': 'batches:update',
  sorting: 'sorting:read',
  'setting-flow': 'settings:read',
  'setting-machine-detail': 'settings:read',
  'setting-machine-load': 'settings:read',
  'hatching-flow': 'hatchings:read',
  'hatching-machine-detail': 'hatchings:read',
  'hatching-machine-load': 'hatchings:read',
  'branch-orders': 'branch-orders:read',

  'admin-users': 'users:read',
  'admin-users-edit': 'users:read',
  'admin-roles': 'roles:read',
  'admin-role-permissions': 'roles:update',
}

// Sidebar-group markers (system-roles.md "Console"): the Sozlamalar and Boshqaruv groups are
// revealed only to holders of the corresponding marker role. Group membership stays a frontend
// decision — the backend has no `configuration` group; the Sozlamalar items live under `hatchery`
// there (answers §2). Per the product decision the marker gates the ROUTE too (not just the nav
// item), so a user without it can't deep-link in and can't land there either. Routes NOT listed
// carry no marker requirement. `expenses` is a FINANCE screen, not part of a marker group — and so
// is `expense-categories` since it moved under Xarajatlar: gating it on the Sozlamalar marker would
// have locked the categories out of the very screen that now opens them.
const ROUTE_REQUIRED_MARKER: Record<string, string> = {
  'settings-branches': SYSTEM_ROLE_CODE.configurationManager,
  'settings-suppliers': SYSTEM_ROLE_CODE.configurationManager,
  'settings-incubation-machines': SYSTEM_ROLE_CODE.configurationManager,
  'settings-egg-standards': SYSTEM_ROLE_CODE.configurationManager,
  'settings-vaccines': SYSTEM_ROLE_CODE.configurationManager,

  'admin-users': SYSTEM_ROLE_CODE.accessManager,
  'admin-users-edit': SYSTEM_ROLE_CODE.accessManager,
  'admin-roles': SYSTEM_ROLE_CODE.accessManager,
  'admin-role-permissions': SYSTEM_ROLE_CODE.accessManager,
}

export const getRequiredMarker = (routeName: string): string | undefined =>
  ROUTE_REQUIRED_MARKER[routeName]

// Order in which a landing route is chosen after login for whatever the user can actually reach.
// Sales leads: super_admin is the only role that can reach both dashboards, and sales is the panel
// they open for day to day work — the incubation dashboard stays one nav click away.
const LANDING_PRIORITY = [
  'sales-dashboard',
  'dashboard',
  'orders',
  'customers',
  'payments',
  'debtors',
  'creditors',
  'reminders',
  'egg-batches',
  'sorting',
  'setting-flow',
  'hatching-flow',
  'branch-orders',
  'sales-egg-batches',
  'expenses',
  'settings-suppliers',
  'admin-users',
  'admin-roles',
]

// Reachable by any authenticated user (no permission/role gate) — used as the last-resort landing
// so a user who maps to no accessible route never hits a redirect loop.
export const NO_ACCESS_ROUTE = 'no-access'

// Routes any authenticated user may open regardless of roles/permissions (self-service + fallback).
const ALWAYS_ALLOWED_ROUTES = new Set<string>([NO_ACCESS_ROUTE, 'account'])

export const getRequiredPermission = (routeName: string): string | undefined =>
  ROUTE_REQUIRED_PERMISSION[routeName]

const isUserRole = (role: unknown): role is UserRole =>
  typeof role === 'string' && USER_ROLES.includes(role as UserRole)

export const normalizeRole = (role: unknown): UserRole | null => {
  if (role === 'sales_agent') return 'sales_manager'
  return isUserRole(role) ? role : null
}

export const normalizeRoles = (roles: unknown): UserRole[] => {
  if (!Array.isArray(roles)) return []

  return Array.from(new Set(roles.map(normalizeRole).filter((role): role is UserRole => Boolean(role))))
}

// Bridge the backend role model to the four legacy route-gating roles (route gating stays
// role-based for this migration phase; permissions[] gating comes later).
// - `isSuperAdmin` maps to super_admin regardless of the code string.
// - separators are normalized (sales-manager -> sales_manager) before matching.
export const normalizeRoleCode = (code: string, isSuperAdmin: boolean): UserRole | null => {
  if (isSuperAdmin) return 'super_admin'
  return normalizeRole(code.replace(/-/g, '_'))
}

export const normalizeRoleCodes = (roles: readonly RoleResponse[]): UserRole[] => {
  if (!Array.isArray(roles)) return []

  return Array.from(
    new Set(
      roles
        .map((role) => normalizeRoleCode(role.code, role.isSuperAdmin))
        .filter((role): role is UserRole => Boolean(role)),
    ),
  )
}

export const hasRole = (roles: readonly UserRole[], role: UserRole) => roles.includes(role)

export const hasAnyRole = (roles: readonly UserRole[], allowedRoles: readonly UserRole[]) =>
  roles.includes('super_admin') || allowedRoles.some((role) => roles.includes(role))

export const getAllowedRolesForRouteName = (routeName: string) => ROUTE_ALLOWED_ROLES[routeName] ?? ADMIN_ROLES

export const getRouteMetaAllowedRoles = (meta: Record<string, unknown>) => {
  const allowedRoles = meta.allowedRoles

  return Array.isArray(allowedRoles) ? normalizeRoles(allowedRoles) : undefined
}

export const canAccessAllowedRoles = (userRoles: readonly UserRole[], allowedRoles?: readonly UserRole[]) =>
  !allowedRoles || hasAnyRole(userRoles, allowedRoles)

export const canAccessRouteName = (
  routeName: string,
  userRoles: readonly UserRole[],
  permissions: readonly string[] = [],
  roleCodes: readonly string[] = [],
) => {
  if (ALWAYS_ALLOWED_ROUTES.has(routeName)) return true

  // Sidebar-group marker gate: super-admin is exempt (holds everything and sees both groups),
  // otherwise the group's marker role must be present before any permission check runs.
  const requiredMarker = getRequiredMarker(routeName)
  if (requiredMarker && !userRoles.includes('super_admin') && !roleCodes.includes(requiredMarker)) {
    return false
  }

  // A backend operation code, when mapped, is the source of truth (super_admin holds them all).
  const requiredPermission = getRequiredPermission(routeName)
  if (requiredPermission) return permissions.includes(requiredPermission)

  // Otherwise fall back to legacy role gating.
  return hasAnyRole(userRoles, getAllowedRolesForRouteName(routeName))
}

export const getFirstAllowedRouteName = (
  roles: readonly UserRole[],
  permissions: readonly string[] = [],
  roleCodes: readonly string[] = [],
) => {
  const reachable = LANDING_PRIORITY.find((routeName) =>
    canAccessRouteName(routeName, roles, permissions, roleCodes),
  )
  return reachable ?? NO_ACCESS_ROUTE
}
